import { useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

const FOCUS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--exed-ink-900)]";

/**
 * Fim da lista: ao aparecer na tela carrega a próxima página. O botão
 * fica para o teclado (e para quem não rola), sem depender do observador.
 */
export function LoadMore({
  hasMore,
  loading,
  onLoadMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
}) {
  const { t } = useTranslation();
  const visible = hasMore || loading;
  const sentinel = useRef<HTMLDivElement>(null);
  const latest = useRef({ hasMore, loading, onLoadMore });
  latest.current = { hasMore, loading, onLoadMore };
  useEffect(() => {
    const element = sentinel.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const {
          hasMore: more,
          loading: busy,
          onLoadMore: load,
        } = latest.current;
        if (entries.some((entry) => entry.isIntersecting) && more && !busy)
          load();
      },
      { rootMargin: "200px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [visible, loading]);
  if (!hasMore && !loading) return null;
  return (
    <div ref={sentinel} className="flex justify-center px-5 py-3">
      {loading ? (
        <span
          role="status"
          className="inline-flex items-center gap-2 text-[13px] text-[var(--exed-ink-500)]"
        >
          <Loader2
            aria-hidden="true"
            className="h-4 w-4 animate-spin motion-reduce:animate-none"
          />
          {t("Loading…")}
        </span>
      ) : (
        <button
          type="button"
          onClick={onLoadMore}
          className={cn(
            "h-8 rounded-md border border-[var(--exed-card-border)] bg-white px-3 text-[13px] font-medium text-[var(--exed-ink-900)] hover:bg-[rgba(255,200,61,0.1)]",
            FOCUS,
          )}
        >
          {t("Load more")}
        </button>
      )}
    </div>
  );
}

/** Rodapé dos painéis de decididos: quantos são, em que página e as setas. */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  if (total === 0) return null;
  const from = page * pageSize + 1;
  const to = Math.min(total, (page + 1) * pageSize);
  const last = Math.max(0, Math.ceil(total / pageSize) - 1);
  const button = cn(
    "inline-flex h-7 w-7 items-center justify-center rounded-md border border-[var(--exed-card-border)] bg-white text-[var(--exed-ink-900)] hover:bg-[rgba(255,200,61,0.1)] disabled:cursor-not-allowed disabled:opacity-40",
    FOCUS,
  );
  return (
    <div className="flex items-center justify-between gap-2 border-t border-[#f0f2f5] px-4 py-2 text-xs text-[var(--exed-ink-500)]">
      <span>
        {t("{{from}}–{{to}} of {{total}}", {
          from: from.toLocaleString("en-US"),
          to: to.toLocaleString("en-US"),
          total: total.toLocaleString("en-US"),
        })}
      </span>
      <div className="flex items-center gap-1.5">
        <span>
          {t("Page {{page}} of {{pages}}", { page: page + 1, pages: last + 1 })}
        </span>
        <button
          type="button"
          aria-label={t("Previous page")}
          disabled={page === 0}
          onClick={() => onPage(page - 1)}
          className={button}
        >
          <ChevronLeft aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          aria-label={t("Next page")}
          disabled={page >= last}
          onClick={() => onPage(page + 1)}
          className={button}
        >
          <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
